// Configuración → Módulos: el admin de la org prende y apaga SUS módulos.
//
// Backend (Orion, org del JWT): GET /org/modules/catalog · GET /org/modules/{key}/impact
// · PUT /org/modules/{key} (apagar exige confirm=true; queda auditado).
// Al cambiar algo se refresca ModulesContext → el menú se actualiza al instante.
// El agente lo toma en su siguiente turno (el gate de módulos se evalúa por turno).

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Bot, History, LayoutGrid, Lock, PanelLeft, RefreshCw, Search, ShieldCheck } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { useModules } from '@/context/ModulesContext';
import {
  getOrgModuleCatalog,
  getOrgModuleImpact,
  setOrgModule,
  type OrgModuleCatalog,
  type OrgModuleImpact,
  type OrgModuleItem,
} from '@/lib/api';
import { moduleRegistry } from '@/modules/registry';
import {
  SOURCE_LABEL,
  describeChange,
  groupModules,
  humanizeTool,
  menuSectionsFor,
  type StateFilter,
} from '../modulesConfig';
import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '../ui';

type Notify = (n: { type: 'success' | 'error' | 'info'; message: string }) => void;

const FILTERS: Array<{ key: StateFilter; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'on', label: 'Activos' },
  { key: 'off', label: 'Apagados' },
];

const fmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
function when(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : fmt.format(d);
}

function Chip({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'primary' | 'warn' }) {
  const cls = {
    muted: 'bg-muted text-muted-foreground',
    primary: 'bg-primary/10 text-primary',
    warn: 'bg-destructive/10 text-destructive',
  }[tone];
  return <span className={`inline-flex max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{children}</span>;
}

function Switch({ on, disabled, busy, label, onChange }: { on: boolean; disabled?: boolean; busy?: boolean; label: string; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      onClick={onChange}
      className="group -m-2 flex shrink-0 items-center p-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-colors motion-reduce:transition-none group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background ${
          on ? 'border-primary bg-primary' : 'border-border bg-muted'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full bg-background shadow transition-transform motion-reduce:transition-none ${
            on ? 'translate-x-6' : 'translate-x-1'
          } ${busy ? 'animate-pulse' : ''}`}
        />
      </span>
    </button>
  );
}

export function ModulosPage({ addNotification }: { addNotification?: Notify }) {
  const { refresh: refreshMenu, archetype } = useModules();
  const [data, setData] = useState<OrgModuleCatalog | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StateFilter>('all');
  const [query, setQuery] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<OrgModuleItem | null>(null);

  const notify = useCallback<Notify>((n) => addNotification?.(n), [addNotification]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOrgModuleCatalog());
      setError(null);
    } catch (e) {
      const err = e as Error & { status?: number };
      setError({ status: err.status, message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const labelOf = useCallback((key: string) => data?.modules.find((m) => m.key === key)?.label ?? key, [data]);
  const groups = useMemo(
    () => (data ? groupModules(data.modules, data.groups, filter, query) : []),
    [data, filter, query],
  );
  const activos = data?.modules.filter((m) => m.enabled).length ?? 0;

  const apply = useCallback(
    async (mod: OrgModuleItem, enabled: boolean, reason?: string) => {
      setBusyKey(mod.key);
      try {
        const out = await setOrgModule(mod.key, enabled, { confirm: !enabled, reason });
        setData((d) =>
          d && out.module ? { ...d, modules: d.modules.map((m) => (m.key === mod.key ? { ...m, ...out.module! } : m)) } : d,
        );
        notify({ type: 'success', message: `${mod.label} ${enabled ? 'encendido' : 'apagado'}.` });
        refreshMenu(); // el menú de esta sesión se actualiza ya
        void getOrgModuleCatalog().then(setData).catch(() => {}); // historial y conteos
      } catch (e) {
        notify({ type: 'error', message: (e as Error).message || 'No se pudo guardar el cambio.' });
      } finally {
        setBusyKey(null);
      }
    },
    [notify, refreshMenu],
  );

  const onToggle = (mod: OrgModuleItem) => {
    if (mod.enabled) setConfirming(mod); // apagar: primero ver qué se pierde
    else void apply(mod, true);
  };

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Módulos"
        subtitle="Prende y apaga lo que usa tu organización. Cada módulo agrega secciones al menú y herramientas a tu agente."
        actions={
          <Button onClick={() => void load()} disabled={loading} aria-label="Actualizar módulos">
            <RefreshCw size={14} aria-hidden className={loading ? 'animate-spin motion-reduce:animate-none' : ''} /> Actualizar
          </Button>
        }
      />

      {loading && !data && <Loading label="Cargando módulos…" />}

      {error && !data && (
        error.status === 403 ? (
          <EmptyState icon={<ShieldCheck size={18} />} title="Solo el administrador puede cambiar los módulos">
            Pídele a quien administra tu organización que encienda o apague módulos desde aquí.
          </EmptyState>
        ) : (
          <div className="space-y-3">
            <ErrorNote>No pudimos cargar los módulos: {error.message}</ErrorNote>
            <Button onClick={() => void load()}>Reintentar</Button>
          </div>
        )
      )}

      {data && (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground tabular-nums">{activos}</span> de{' '}
              <span className="tabular-nums">{data.modules.length}</span> módulos activos
            </p>
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <label className="relative block w-full sm:max-w-xs">
                <span className="sr-only">Buscar módulo</span>
                <Search size={15} aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar módulo…"
                  className="w-full rounded-xl border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <div role="tablist" aria-label="Filtrar por estado" className="flex gap-1 rounded-xl border border-border bg-card p-1">
                {FILTERS.map((f) => {
                  const on = filter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      onClick={() => setFilter(f.key)}
                      className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors sm:flex-none ${
                        on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {groups.length === 0 && (
            <EmptyState icon={<LayoutGrid size={18} />} title="Ningún módulo coincide">
              Prueba con otra palabra o cambia el filtro.
            </EmptyState>
          )}

          <div className="space-y-4">
            {groups.map((g) => (
              <Card key={g.group} title={g.group} titleId={`grp-${g.group}`} bodyClassName="!pt-1">
                <ul className="divide-y divide-border">
                  {g.items.map((m) => {
                    const secciones = menuSectionsFor(m.key, moduleRegistry, archetype);
                    const icono = moduleRegistry.find((r) => r.backendModuleKey === m.key)?.icon ?? <LayoutGrid size={18} />;
                    return (
                      <li key={m.key} className="flex items-start gap-3 py-3.5">
                        <span
                          aria-hidden
                          className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                            m.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {icono}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span id={`mod-${m.key}`} className="text-sm font-semibold text-foreground">{m.label}</span>
                            {m.enabled ? <Chip tone="primary">Activo</Chip> : <Chip>Apagado</Chip>}
                          </div>
                          {m.description && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{m.description}</p>}
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <Chip>{SOURCE_LABEL[m.source] ?? m.source}</Chip>
                            {secciones.length > 0 && (
                              <Chip>
                                <PanelLeft size={11} aria-hidden />{' '}
                                <span className="truncate" title={secciones.map((s) => s.name).join(' · ')}>
                                  {secciones.slice(0, 2).map((s) => s.name).join(' · ')}
                                  {secciones.length > 2 && ` +${secciones.length - 2} más`}
                                </span>
                              </Chip>
                            )}
                            {m.agent_tools > 0 && (
                              <Chip>
                                <Bot size={11} aria-hidden /> {m.agent_tools} {m.agent_tools === 1 ? 'herramienta' : 'herramientas'} del agente
                              </Chip>
                            )}
                            {!m.available && (
                              <Chip tone="warn">
                                <Lock size={11} aria-hidden /> {m.unavailable_reason || 'No incluido en tu plan'}
                              </Chip>
                            )}
                          </div>
                          {m.requirement && <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">ⓘ {m.requirement}</p>}
                        </div>
                        <Switch
                          on={m.enabled}
                          busy={busyKey === m.key}
                          disabled={(!m.available && !m.enabled) || (busyKey !== null && busyKey !== m.key)}
                          label={`${m.enabled ? 'Apagar' : 'Encender'} ${m.label}`}
                          onChange={() => onToggle(m)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}

            <Card title={<span className="inline-flex items-center gap-2"><History size={16} aria-hidden /> Cambios recientes</span>} titleId="mod-historial">
              {data.history.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún no hay cambios registrados. Cada vez que alguien prenda o apague un módulo quedará aquí.</p>
              ) : (
                <ul className="space-y-2">
                  {data.history.map((c, i) => (
                    <li key={`${c.at}-${c.key}-${i}`} className="flex flex-col gap-0.5 text-sm sm:flex-row sm:items-baseline sm:gap-3">
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{when(c.at)}</span>
                      <span className="text-foreground">
                        {describeChange(c, labelOf)}
                        {c.reason && <span className="text-muted-foreground"> — «{c.reason}»</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}

      {confirming && (
        <ConfirmOff
          mod={confirming}
          archetype={archetype}
          onCancel={() => setConfirming(null)}
          onConfirm={(reason) => {
            const m = confirming;
            setConfirming(null);
            void apply(m, false, reason);
          }}
        />
      )}
    </div>
  );
}

function ConfirmOff({
  mod,
  archetype,
  onCancel,
  onConfirm,
}: {
  mod: OrgModuleItem;
  archetype: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [impact, setImpact] = useState<OrgModuleImpact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const secciones = useMemo(() => menuSectionsFor(mod.key, moduleRegistry, archetype), [mod.key, archetype]);

  useEffect(() => {
    let vivo = true;
    getOrgModuleImpact(mod.key, false)
      .then((r) => vivo && setImpact(r))
      .catch((e: Error) => vivo && setError(e.message));
    return () => {
      vivo = false;
    };
  }, [mod.key]);

  return (
    <Modal
      open
      inShell
      size="md"
      onClose={onCancel}
      title={`¿Apagar ${mod.label}?`}
      footer={
        <>
          <Button onClick={onCancel}>Cancelar</Button>
          <Button variant="danger" disabled={!impact && !error} onClick={() => onConfirm(reason)}>
            Apagar módulo
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-muted-foreground">Esto aplica para toda tu organización. Puedes volver a encenderlo cuando quieras.</p>

        <section aria-labelledby="off-menu">
          <h3 id="off-menu" className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground">
            <PanelLeft size={15} aria-hidden /> Del menú desaparece
          </h3>
          {secciones.length === 0 ? (
            <p className="text-muted-foreground">Nada: este módulo no tiene secciones propias en el menú.</p>
          ) : (
            <ul className="space-y-1">
              {secciones.map((s) => (
                <li key={s.key} className="rounded-lg bg-muted px-3 py-2">
                  <span className="font-medium text-foreground">{s.name}</span>
                  {s.tabs.length > 0 && <span className="text-muted-foreground"> — {s.tabs.join(', ')}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="off-agent">
          <h3 id="off-agent" className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground">
            <Bot size={15} aria-hidden /> Tu agente deja de poder usar
          </h3>
          {!impact && !error && <Loading label="Revisando qué usa tu agente…" />}
          {error && <ErrorNote>No pudimos calcular el impacto en el agente ({error}). Si continúas, se apaga igual.</ErrorNote>}
          {impact && impact.agent_tools_count === 0 && (
            <p className="text-muted-foreground">Ninguna herramienta: otros módulos encendidos siguen cubriendo lo que usa tu agente.</p>
          )}
          {impact && impact.agent_uses_30d > 0 && (
            <p className="mb-2 flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-destructive">
              <AlertTriangle size={15} aria-hidden className="mt-0.5 shrink-0" />
              <span>
                Tu agente usó estas herramientas <strong className="tabular-nums">{impact.agent_uses_30d}</strong>{' '}
                {impact.agent_uses_30d === 1 ? 'vez' : 'veces'} en los últimos 30 días. Las conversaciones que dependan de ellas ya no podrán hacerlo.
              </span>
            </p>
          )}
          {impact && impact.agent_tools_count > 0 && (
            <ul className="max-h-56 space-y-1 overflow-y-auto pr-1">
              {impact.agent_tools.map((t) => (
                <li key={t.tool_id} className="flex items-start justify-between gap-3 rounded-lg border border-border px-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">{humanizeTool(t.name)}</span>
                    {t.summary && <span className="block truncate text-xs text-muted-foreground">{t.summary}</span>}
                  </span>
                  {t.usos_30d > 0 && <Chip tone="warn">{t.usos_30d} usos</Chip>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Motivo (opcional, queda en el historial)</span>
          <input
            type="text"
            value={reason}
            maxLength={300}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej. ya no usamos facturación aquí"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
      </div>
    </Modal>
  );
}
